import apiClient from './client';
import type {
  NotificationRule,
  NotificationRuleCreate,
  NotificationRuleUpdate,
  NotificationRuleListItem,
  Notification,
  NotificationListItem,
  PaginatedResponse,
  UnreadCountResponse,
} from '@/types/data';

// 通知ルールAPI
export const notificationRulesApi = {
  // ルール一覧取得
  getRules: async (companyId: number, isActive?: boolean): Promise<NotificationRuleListItem[]> => {
    const params: Record<string, unknown> = { company_id: companyId };
    if (isActive !== undefined) {
      params.is_active = isActive;
    }
    const response = await apiClient.get<NotificationRuleListItem[]>('/api/notifications/rules', { params });
    return response.data;
  },

  // ルール作成
  createRule: async (data: NotificationRuleCreate): Promise<NotificationRule> => {
    const response = await apiClient.post<NotificationRule>('/api/notifications/rules', data);
    return response.data;
  },

  // ルール詳細取得
  getRule: async (ruleId: number): Promise<NotificationRule> => {
    const response = await apiClient.get<NotificationRule>(`/api/notifications/rules/${ruleId}`);
    return response.data;
  },

  // ルール更新
  updateRule: async (ruleId: number, data: NotificationRuleUpdate): Promise<NotificationRule> => {
    const response = await apiClient.put<NotificationRule>(`/api/notifications/rules/${ruleId}`, data);
    return response.data;
  },

  // ルール削除
  deleteRule: async (ruleId: number): Promise<void> => {
    await apiClient.delete(`/api/notifications/rules/${ruleId}`);
  },
};

// 通知API
export interface NotificationsParams {
  company_id: number;
  is_read?: boolean;
  notification_type?: string;
  page?: number;
  per_page?: number;
}

export const notificationsApi = {
  // 通知一覧取得
  getNotifications: async (params: NotificationsParams): Promise<PaginatedResponse<NotificationListItem>> => {
    const response = await apiClient.get<PaginatedResponse<NotificationListItem>>('/api/notifications', { params });
    return response.data;
  },

  // 未読件数取得
  getUnreadCount: async (companyId: number): Promise<UnreadCountResponse> => {
    const response = await apiClient.get<UnreadCountResponse>('/api/notifications/unread/count', {
      params: { company_id: companyId },
    });
    return response.data;
  },

  // 通知詳細取得
  getNotification: async (notificationId: number): Promise<Notification> => {
    const response = await apiClient.get<Notification>(`/api/notifications/${notificationId}`);
    return response.data;
  },

  // 通知を既読にする
  markAsRead: async (notificationId: number): Promise<void> => {
    await apiClient.patch(`/api/notifications/${notificationId}/read`);
  },

  // 複数の通知を既読にする
  markMultipleAsRead: async (companyId: number, notificationIds: number[]): Promise<{ count: number }> => {
    const response = await apiClient.post<{ count: number }>('/api/notifications/mark-read', {
      notification_ids: notificationIds,
    }, {
      params: { company_id: companyId },
    });
    return response.data;
  },

  // 全て既読にする
  markAllAsRead: async (companyId: number): Promise<{ count: number }> => {
    const response = await apiClient.post<{ count: number }>('/api/notifications/mark-all-read', null, {
      params: { company_id: companyId },
    });
    return response.data;
  },

  // ルール評価（手動トリガー）
  evaluateRules: async (companyId: number): Promise<{ notifications_created: number }> => {
    const response = await apiClient.post<{ notifications_created: number }>('/api/notifications/evaluate', null, {
      params: { company_id: companyId },
    });
    return response.data;
  },
};
